import React, { useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import ConceptDetailsSection from 'screens/FDP/components/ConceptDetails';
import NotesSection from 'screens/FDPScreen/components/NotesSection';
import classes from './salesPlanView.module.scss';
import {
  mockMSPlanViewData,
  mockSalesPlanViewData,
  mockSalesSummary,
  mockConceptDetails,
  mockContractDetails,
  mockNotesData,
  getFutureMonths15,
} from '../salesPlanMockData';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CotOption = { label: string; value: string };

const COT_OPTIONS: CotOption[] = [
  { label: 'CMOP', value: 'cmop' },
  { label: 'VA Facility', value: 'va facility' },
  { label: 'TMOP', value: 'tmop' },
  { label: 'MTF', value: 'mtf' },
];

// Mock planning period dropdown options (mirrors salesplanPlanningPeriod from store)
const PLAN_PERIOD_OPTIONS = [
  { label: 'Apr 2026 - Mar 2027', value: 'period1' },
  { label: 'May 2026 - Apr 2027', value: 'period2' },
];

// Pre-compute months once
const PLANNING_MONTHS = getFutureMonths15();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getSalesSummaryVal = (
  cotValue: string,
  metric: 'YTD' | '3MA' | 'PM',
  category: 'direct' | 'indirect',
): string => {
  const row = mockSalesSummary.find((r) => r.COT === cotValue);
  if (!row) return '-';
  if (metric === 'YTD') {
    return category === 'direct'
      ? `${row.YearToDateDirectShare ?? '-'}%`
      : `${row.YearToDateIndirectShare ?? '-'}%`;
  }
  if (metric === '3MA') {
    return category === 'direct'
      ? `${row.LastThreeMonthDirectShare ?? '-'}%`
      : `${row.LastThreeMonthIndirectShare ?? '-'}%`;
  }
  return category === 'direct'
    ? `${row.LastMonthDirectShare ?? '-'}%`
    : `${row.LastMonthIndirectShare ?? '-'}%`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SalesPlanView: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(true); // true = Market Share Plan tab
  const [directCOT, setDirectCOT] = useState<CotOption>(COT_OPTIONS[0]);
  const [salesPlanPeriod, setSalesPlanPeriod] = useState(PLAN_PERIOD_OPTIONS[0]);

  // ---------------------------------------------------------------------------
  // Sales Summary box
  // ---------------------------------------------------------------------------

  const renderSalesSummary = (cotValue: string, category: 'direct' | 'indirect') => (
    <div className={classes.saleDetails}>
      <div>
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            paddingLeft: '3rem',
          }}
        >
          YTD:
          <span>{getSalesSummaryVal(cotValue, 'YTD', category)}</span>
        </div>
        <div className={classes.vertical} />
        <div style={{ fontSize: '14px', fontWeight: '600' }}>
          3MA:
          <span>{getSalesSummaryVal(cotValue, '3MA', category)}</span>
        </div>
        <div className={classes.vertical} />
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            paddingRight: '3rem',
          }}
        >
          PM:
          <span>{getSalesSummaryVal(cotValue, 'PM', category)}</span>
        </div>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // COT radio buttons (Direct only — Indirect has none per actual app)
  // ---------------------------------------------------------------------------

  const renderCOTRadios = () => (
    <div className={classes.directRadioGrp}>
      <span>
        <input
          type="radio"
          name="directRadio"
          checked={directCOT.label === 'CMOP'}
          onChange={() => setDirectCOT({ label: 'CMOP', value: 'cmop' })}
        />
        <label>CMOP</label>
      </span>
      <span>
        <input
          type="radio"
          name="directRadio"
          checked={directCOT.label === 'VA Facility'}
          onChange={() => setDirectCOT({ label: 'VA Facility', value: 'va facility' })}
        />
        <label>VA Facility</label>
      </span>
      <span>
        <input
          type="radio"
          name="directRadio"
          checked={directCOT.label === 'TMOP'}
          onChange={() => setDirectCOT({ label: 'TMOP', value: 'tmop' })}
        />
        <label>TMOP</label>
      </span>
      <span>
        <input
          type="radio"
          name="directRadio"
          checked={directCOT.label === 'MTF'}
          onChange={() => setDirectCOT({ label: 'MTF', value: 'mtf' })}
        />
        <label>MTF</label>
      </span>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Market Share Plan table (read-only: Planning Period, Strat Code, TMS, MS Plan)
  // ---------------------------------------------------------------------------

  const renderMSPlanTable = (category: 'direct' | 'indirect') => {
    const rows = mockMSPlanViewData.filter(
      (r) => r.cot === directCOT.value && r.category === category,
    );

    return (
      <div className={classes.planTable}>
        <table>
          <thead>
            <th>
              <td>Planning Period</td>
            </th>
            <th>
              <td>Strat Code</td>
            </th>
            <th>
              <td>TMS</td>
            </th>
            <th>
              <td>MS Plan</td>
            </th>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.planningPeriod}>
                <td>{row.planningPeriodLabel}</td>
                <td>{row.stratCode}</td>
                <td>{row.tmsValue}</td>
                <td>{row.msPlan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Sales Plan table (read-only: Planning Period, Market Forecast, Adj Market
  //   Forecast, TMS, MS Plan, EU Sales Plan)
  // ---------------------------------------------------------------------------

  const renderSalesPlanTable = (category: 'direct' | 'indirect') => {
    const rows = mockSalesPlanViewData.filter(
      (r) => r.cot === directCOT.value && r.category === category,
    );

    return (
      <div className={classes.planTable}>
        <table>
          <thead>
            <th>
              <td>Planning Period</td>
            </th>
            <th>
              <td>Market Forecast</td>
            </th>
            <th>
              <td>Adj Market Forecast</td>
            </th>
            <th>
              <td>TMS</td>
            </th>
            <th>
              <td>MS Plan</td>
            </th>
            <th>
              <td>EU Sales Plan</td>
            </th>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.planningPeriod}>
                <td>{row.planningPeriodLabel}</td>
                <td style={{ color: '#7B4B9F', fontWeight: '600' }}>
                  {row.marketForecast.toLocaleString('en-US')}
                </td>
                <td style={{ color: '#7B4B9F', fontWeight: '600' }}>
                  {row.adjMarketForecast.toLocaleString('en-US')}
                </td>
                <td>{row.tmsValue}</td>
                <td>{row.msPlan}</td>
                <td>{row.euSalesPlan.toLocaleString('en-US')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Direct section (with COT radios)
  // ---------------------------------------------------------------------------

  const renderDirectSection = (tableType: 'msplan' | 'salesplan') => (
    <div className={classes.directContainer}>
      <div>
        <p
          style={{
            fontSize: '20px',
            marginLeft: '1rem',
            fontWeight: '600',
            color: '#4D2E6D',
          }}
        >
          Direct
        </p>
        {renderSalesSummary(directCOT.value, 'direct')}
        {renderCOTRadios()}
      </div>
      <div>
        {tableType === 'msplan'
          ? renderMSPlanTable('direct')
          : renderSalesPlanTable('direct')}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Indirect section (no COT radios — same COT as direct per actual app)
  // ---------------------------------------------------------------------------

  const renderIndirectSection = (tableType: 'msplan' | 'salesplan') => (
    <div className={classes.directContainer}>
      <div>
        <p
          style={{
            fontSize: '20px',
            marginLeft: '1rem',
            fontWeight: '600',
            color: '#4D2E6D',
          }}
        >
          Indirect
        </p>
        {renderSalesSummary(directCOT.value, 'indirect')}
        <div />
      </div>
      <div>
        {tableType === 'msplan'
          ? renderMSPlanTable('indirect')
          : renderSalesPlanTable('indirect')}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className={classes.targetMarketContainer}>
      {/* Heading + ConceptDetailsSection */}
      <div className={classes.planningContainer}>
        <div className={classes.tmsHeading}>
          <h2>Sales Plan Forecast</h2>
        </div>
        <ConceptDetailsSection
          path={`edit?gfc=${mockConceptDetails.gfc}`}
          label="New Plan"
          loading={false}
          isDisabled={false}
          conceptDetails={mockConceptDetails}
          contractDetails={mockContractDetails}
        />
      </div>

      {/* TabView (grows) + NotesSection (right) */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div
          style={{
            width: '90rem',
            borderRadius: '8px',
            flexGrow: 1,
          }}
        >
          <TabView scrollable tabIndex={tabIndex ? 0 : 1}>
            {/* ----------------------------------------------------------------
                Tab 1 — Market Share Plan
            ---------------------------------------------------------------- */}
            <TabPanel
              header={
                <div
                  onClick={() => setTabIndex(true)}
                  className={`${tabIndex ? classes.headerSelected : classes.headerUnselected} ${classes.tabIndex}`}
                >
                  Market Share Plan
                </div>
              }
            >
              <div className={classes.planDetailsContainer}>
                <h4>
                  <div>
                    <span>
                      Plan {PLANNING_MONTHS[0].label} - {PLANNING_MONTHS[14].label}
                    </span>
                  </div>
                </h4>

                {/* Direct */}
                {renderDirectSection('msplan')}

                <hr />

                {/* Indirect */}
                {renderIndirectSection('msplan')}
              </div>
            </TabPanel>

            {/* ----------------------------------------------------------------
                Tab 2 — Sales Plan
            ---------------------------------------------------------------- */}
            <TabPanel
              header={
                <div
                  onClick={() => setTabIndex(false)}
                  className={`${!tabIndex ? classes.headerSelected : classes.headerUnselected} ${classes.tabIndex}`}
                >
                  Sales Plan
                </div>
              }
            >
              <div className={classes.planDetailsContainer}>
                <h4>
                  <div>
                    <span>Choose Plan:</span>
                  </div>
                  <div className={classes.planningPeriodDropdown}>
                    <Dropdown
                      value={salesPlanPeriod}
                      options={PLAN_PERIOD_OPTIONS}
                      optionLabel="label"
                      onChange={(e) => setSalesPlanPeriod(e.value)}
                      style={{ width: '15rem' }}
                    />
                  </div>
                </h4>

                {/* Direct */}
                {renderDirectSection('salesplan')}

                <hr />

                {/* Indirect */}
                {renderIndirectSection('salesplan')}
              </div>
            </TabPanel>
          </TabView>
        </div>

        <NotesSection
          mode="view"
          isNotesDataLoading={false}
          notesData={mockNotesData}
        />
      </div>
    </div>
  );
};

export default SalesPlanView;
